import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SectionDecor } from "@/components/SectionDecor";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { BentoGallery, ServicesGrid } from "@/routes/index";
import { caCities, company } from "@/data/site";
import { getGroupedCities } from "@/data/cityRegions";
import areasHero from "@/assets/heroes/areas-rooftops.jpg";
import realitiesImg from "@/assets/mckay/roofing-service.webp.asset.json";
import gallery1 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import gallery2 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import gallery3 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";
import gallery4 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import gallery5 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import gallery6 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import gallery7 from "@/assets/gallery-new/6940e3a997c0238f425981a9-2.webp.asset.json";
import gallery8 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";

export const Route = createFileRoute("/areas/california/")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar in California – McKay Roofing & Solar" },
      { name: "description", content: "McKay Roofing & Solar serves homeowners across Southern California. Roof repair, replacement, and solar installation across Southern California and surrounding cities." },
      { name: "keywords", content: "roofing California, solar installation California, Southern California roofing, McKay Roofing & Solar, roof repair California" },
      { property: "og:title", content: "Roofing & Solar in California – McKay Roofing & Solar" },
      { property: "og:description", content: "Local roofing and solar experts serving Southern California." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/areas/california" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/areas/california" }],
  }),
  component: CaliforniaPage,
});

function CaliforniaPage() {
  const gallery = [
    { src: gallery1.url, alt: "Solar panel installation on a shingle roof in California" },
    { src: gallery2.url, alt: "Coastal California solar installation" },
    { src: gallery3.url, alt: "Solar array on a tile roof" },
    { src: gallery4.url, alt: "Multi-section solar installation on a California home" },
    { src: gallery5.url, alt: "Completed solar installation on a residential home" },
    { src: gallery6.url, alt: "Flat-roof solar array on a California home" },
    { src: gallery7.url, alt: "Roof replacement in progress with fresh underlayment" },
    { src: gallery8.url, alt: "Residential solar installation on a shingle roof" },
  ];
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={areasHero} alt="Aerial drone view of Southern California rooftops served by McKay Roofing & Solar" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36 lg:px-8 lg:py-44">
            <h1 className="text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">
              Roofing &amp; Solar Across Southern California
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              From our Carlsbad base, McKay Roofing &amp; Solar serves homeowners and businesses across Southern California and the surrounding California communities – with the same crew, the same standards, and the same honest guidance.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                <Link to="/contact">Get Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
              Local roofing and solar experts, built for California weather
            </h2>
            <p className="mt-5 text-muted-foreground">
              California roofs face a lot – nonstop sun, dry heat, coastal salt, and the occasional heavy storm. We install roofing and solar systems that hold up here, and we're honest about what your roof actually needs. Whether it's a small repair, a full replacement, or a solar install paired with a new roof, our team handles it end to end.
            </p>
          </div>
        </section>

        {/* Deep SEO copy */}
        <section className="bg-muted/40">
          <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div className="relative overflow-hidden rounded-2xl shadow-elegant">
              <img
                src={realitiesImg.url}
                alt="McKay Roofing & Solar crew working on a Southern California roof"
                loading="lazy"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </div>
            <div>
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">Roofing and solar realities across Southern California</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/85">
              <p>
                Sun does more damage to roofs here than storms do. Under year-round UV, underlayment dries out and shingle granules wear away early, especially on the unshaded south-facing slopes common across Winter Gardens, Poway and Fallbrook.
              </p>
              <p>
                Santa Ana winds are the other factor out east, lifting loose flashing and shingle tabs that coastal roofs rarely deal with. And because solar only pays off if the roof lasts the life of the system, we check roof condition first and handle city permitting on every job.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <p className="text-sm font-semibold text-muted-foreground">Ready to talk about your roof or solar project?</p>
              <Button asChild className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get a Free Quote</Link>
              </Button>
            </div>
            </div>
          </div>
        </section>

        {/* Services (shared homepage section) */}
        <ServicesGrid />

        {/* Why choose */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Why California Homeowners Choose McKay</h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { t: "40+ years in business", d: "Four decades of roofing and solar experience across Southern California." },
              { t: "Licensed &amp; insured", d: "California license #1130793. Fully insured for your protection." },
              { t: "Roofing + solar together", d: "One team handles both, so your roof and system work as one." },
              { t: "Honest guidance", d: "We tell you when a repair is enough – no upselling to a full replacement." },
              { t: "Local crew", d: "Based in Carlsbad. Same crew shows up on every project." },
              { t: "150+ five-star reviews", d: "Google reviews from homeowners across Southern California." },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-primary" />
                <div className="mt-3 text-lg font-bold text-primary" dangerouslySetInnerHTML={{ __html: b.t }} />
                <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Gallery preview */}
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Recent Work Across California</h2>
              <p className="mt-4 text-muted-foreground">Roof replacements, repairs, and solar installs from around the region.</p>
            </div>
            <BentoGallery images={gallery} />
          </div>
        </section>

        {/* Regional hubs */}
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Our Regional Hubs</h2>
              <p className="mt-4 text-muted-foreground">Each hub covers a group of cities we run crews through every week.</p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {getGroupedCities().map((r) => (
                <Link
                  key={r.id}
                  to="/areas/hubs/$hub"
                  params={{ hub: r.id }}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <h3 className="font-serif text-xl font-bold">{r.label} Hub</h3>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-primary transition-colors group-hover:text-secondary">
                    {r.cities.length} cities <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Cities grid */}
        <section className="relative overflow-hidden bg-muted/40">
          <SectionDecor variant="house" side="left" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Cities We Serve in California</h2>
              <p className="mt-4 text-muted-foreground">Pick your city for local roofing and solar information.</p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {caCities.map((c) => (
                <Link
                  key={c.slug}
                  to="/areas/california/$city"
                  params={{ city: c.slug }}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <h3 className="font-serif text-xl font-bold">{c.name}</h3>
                  </div>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-primary transition-colors group-hover:text-secondary">
                    View city <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTABlock
          title="Ready for a free California roofing or solar estimate?"
          subtitle="Tell us about your project. We usually respond same day."
        />
      </main>
      <SiteFooter />
    </div>
  );
}